import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight, CheckCircle2, Circle, Download, FileText, Menu, FolderOpen, Award } from "lucide-react";
import { toast } from "sonner";
import MuxLessonPlayer from "../components/MuxLessonPlayer";
import VideoWatermark from "../components/VideoWatermark";

const Player = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, API } = useAuth();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    fetchCourse();
    fetchProgress();
  }, [courseId]);

  useEffect(() => {
    if (course && course.modules && course.modules.length > 0) {
      if (!currentLesson) {
        const firstModule = course.modules[0];
        if (firstModule.lessons && firstModule.lessons.length > 0) {
          setCurrentLesson(firstModule.lessons[0]);
          setCurrentModuleIndex(0);
          setCurrentLessonIndex(0);
        }
      }
    }
  }, [course]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setCourse(response.data);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 403) {
        toast.error("Este curso es solo para suscriptores PRO");
      } else if (status === 404) {
        toast.error("Curso no encontrado");
      } else {
        toast.error("Error al cargar el curso");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/progress/${courseId}`);
      setProgress(response.data);
    } catch (error) {
      console.error("Error fetching progress:", error);
    }
  };

  const markAsCompleted = async (lessonId) => {
    try {
      await axios.post(`${API}/progress`, {
        course_id: courseId,
        lesson_id: lessonId,
        completed: true,
      });
      await fetchProgress();
      toast.success("Leccion marcada como completada");
    } catch (error) {
      toast.error("Error al actualizar progreso");
    }
  };

  const isLessonCompleted = (lessonId) => {
    return progress?.completed_lessons?.includes(lessonId) || false;
  };

  const selectLesson = (moduleIndex, lessonIndex) => {
    const lesson = course.modules[moduleIndex].lessons[lessonIndex];
    setCurrentLesson(lesson);
    setCurrentModuleIndex(moduleIndex);
    setCurrentLessonIndex(lessonIndex);
  };

  const getTotalLessons = () => {
    if (!course) return 0;
    return course.modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);
  };

  const getCompletedCount = () => {
    return progress?.completed_lessons?.length || 0;
  };

  const getProgressPercentage = () => {
    const total = getTotalLessons();
    if (total === 0) return 0;
    return Math.round((getCompletedCount() / total) * 100);
  };

  const getNextLesson = () => {
    if (!course) return null;
    const currentModule = course.modules[currentModuleIndex];
    if (currentLessonIndex < currentModule.lessons.length - 1) {
      return currentModule.lessons[currentLessonIndex + 1];
    } else if (currentModuleIndex < course.modules.length - 1) {
      const nextMod = course.modules[currentModuleIndex + 1];
      if (nextMod.lessons?.length > 0) return nextMod.lessons[0];
    }
    return null;
  };

  const getPrevLesson = () => {
    if (!course) return null;
    if (currentLessonIndex > 0) {
      return course.modules[currentModuleIndex].lessons[currentLessonIndex - 1];
    } else if (currentModuleIndex > 0) {
      const prevMod = course.modules[currentModuleIndex - 1];
      if (prevMod.lessons?.length > 0) return prevMod.lessons[prevMod.lessons.length - 1];
    }
    return null;
  };

  const goToNextLesson = () => {
    if (!course) return;
    const currentModule = course.modules[currentModuleIndex];
    if (currentLessonIndex < currentModule.lessons.length - 1) {
      selectLesson(currentModuleIndex, currentLessonIndex + 1);
    } else if (currentModuleIndex < course.modules.length - 1) {
      selectLesson(currentModuleIndex + 1, 0);
    }
  };

  const goToPreviousLesson = () => {
    if (!course) return;
    if (currentLessonIndex > 0) {
      selectLesson(currentModuleIndex, currentLessonIndex - 1);
    } else if (currentModuleIndex > 0) {
      const prevModule = course.modules[currentModuleIndex - 1];
      selectLesson(currentModuleIndex - 1, prevModule.lessons.length - 1);
    }
  };

  const getVideoEmbedUrl = (url, type) => {
    if (!url) return "";
    if (type === "youtube") {
      // Soporta: youtube.com/watch?v=XXX, youtu.be/XXX, youtube.com/embed/XXX, con o sin parámetros
      let videoId = "";
      const patterns = [
        /(?:youtube\.com\/watch\?(?:.*&)?v=)([^&\s]+)/,
        /(?:youtu\.be\/)([^?&\s]+)/,
        /(?:youtube\.com\/embed\/)([^?&\s]+)/,
        /(?:youtube\.com\/shorts\/)([^?&\s]+)/,
      ];
      for (const p of patterns) {
        const m = url.match(p);
        if (m && m[1]) {
          videoId = m[1];
          break;
        }
      }
      if (!videoId) return url; // fallback
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    } else if (type === "vimeo") {
      // Soporta: vimeo.com/XXX, player.vimeo.com/video/XXX, con o sin hash
      const m = url.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/);
      const videoId = m && m[1] ? m[1] : url.split("/").pop().split("?")[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <p className="text-white text-xl font-semibold mb-3">No se pudo cargar el curso</p>
          <p className="text-white/60 text-sm mb-6">
            El curso no existe o no tienes acceso. Verifica que estás inscrito o que tu suscripción esté activa.
          </p>
          <button
            onClick={() => navigate("/mis-cursos")}
            className="bg-[#E50914] hover:bg-[#E50914]/90 text-white px-5 py-2 rounded-md font-semibold"
          >
            Volver a Mis Cursos
          </button>
        </div>
      </div>
    );
  }

  if (!course.modules || course.modules.length === 0 ||
      !course.modules[0].lessons || course.modules[0].lessons.length === 0) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center px-4">
        <div className="max-w-lg text-center">
          <p className="text-[#E50914] text-sm uppercase tracking-widest font-bold mb-3">Curso en construcción</p>
          <h2 className="text-white text-2xl font-bold mb-3">{course.title}</h2>
          <p className="text-white/70 text-sm mb-2">
            Este curso aún no tiene lecciones publicadas.
          </p>
          <p className="text-white/50 text-xs mb-6">
            Pronto el instructor agregará los módulos y videos. Vuelve más tarde para empezar a aprender.
          </p>
          <button
            onClick={() => navigate("/cursos")}
            className="bg-[#E50914] hover:bg-[#E50914]/90 text-white px-5 py-2 rounded-md font-semibold"
            data-testid="back-to-catalog-btn"
          >
            Ver otros cursos
          </button>
        </div>
      </div>
    );
  }

  if (!currentLesson) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-white text-xl">Cargando lección...</div>
      </div>
    );
  }

  const pct = getProgressPercentage();
  const prevLesson = getPrevLesson();
  const nextLesson = getNextLesson();

  return (
    <div className="h-screen bg-[#1a1a1a] flex flex-col overflow-hidden">
      {/* Top Header Bar */}
      <div className="bg-[#111] border-b border-white/10 px-4 h-14 flex items-center justify-between flex-shrink-0">
        {/* Left: Progress + Course Info */}
        <div className="flex items-center gap-4">
          {/* Progress Circle */}
          <div className="relative w-12 h-12 flex-shrink-0">
            <svg className="w-12 h-12 -rotate-90" viewBox="0 0 44 44">
              <circle cx="22" cy="22" r="18" fill="none" stroke="#333" strokeWidth="3" />
              <circle
                cx="22" cy="22" r="18" fill="none"
                stroke="#E50914" strokeWidth="3"
                strokeDasharray={`${(pct / 100) * 113} 113`}
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">
              {pct}%
            </span>
          </div>
          <div className="hidden sm:block">
            <h1 className="text-white font-bold text-sm leading-tight truncate max-w-[200px]" data-testid="course-title-header">
              {course.title}
            </h1>
            <p className="text-white/50 text-xs">
              {getCompletedCount()} de {getTotalLessons()} lecciones completadas
            </p>
          </div>
        </div>

        {/* Center: Menu Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white/60 hover:text-white p-2"
          data-testid="toggle-sidebar-btn"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Right: Prev / Next Navigation */}
        <div className="flex items-center gap-6">
          {pct === 100 && (
            <Button
              onClick={() => navigate("/mis-datos")}
              size="sm"
              className="bg-[#E50914] hover:bg-[#E50914]/90 text-white text-xs font-bold shadow-[0_0_20px_rgba(229,9,20,0.4)] hidden sm:flex"
              data-testid="get-certificate-btn"
            >
              <Award className="w-4 h-4 mr-1" />
              Obtener certificado
            </Button>
          )}
          <button
            onClick={goToPreviousLesson}
            disabled={!prevLesson}
            className="flex items-center gap-1 text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="previous-lesson-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="text-sm hidden md:inline">Anterior</span>
          </button>
          <button
            onClick={goToNextLesson}
            disabled={!nextLesson}
            className="flex items-center gap-2 text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="next-lesson-btn"
          >
            <div className="text-right hidden md:block">
              <p className="text-xs text-white/40">Proximo</p>
              <p className="text-xs text-white truncate max-w-[180px]">
                {nextLesson?.title || "—"}
              </p>
            </div>
            <div className="w-6 h-6 rounded-full border border-[#E50914] flex items-center justify-center">
              <ChevronRight className="w-3 h-3 text-[#E50914]" />
            </div>
          </button>
        </div>
      </div>

      {/* Main Content: 3-column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT SIDEBAR: Modules & Lessons */}
        {sidebarOpen && (
          <div className="w-64 lg:w-72 bg-[#222] border-r border-white/10 overflow-y-auto flex-shrink-0" data-testid="course-sidebar">
            {course.modules.map((module, moduleIndex) => (
              <div key={module.id}>
                {/* Module Header */}
                <div className="px-4 py-3 border-b border-white/10 bg-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-white/60" />
                    <span className="text-white/50 text-xs font-medium">Modulo {moduleIndex + 1}</span>
                  </div>
                  <h3 className="text-white font-bold text-sm mt-1">{module.title}</h3>
                </div>

                {/* Lessons */}
                {module.lessons?.map((lesson, lessonIndex) => {
                  const isCompleted = isLessonCompleted(lesson.id);
                  const isCurrent = currentModuleIndex === moduleIndex && currentLessonIndex === lessonIndex;

                  return (
                    <button
                      key={lesson.id}
                      onClick={() => selectLesson(moduleIndex, lessonIndex)}
                      className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors ${
                        isCurrent
                          ? "bg-[#E50914]"
                          : "hover:bg-white/5"
                      }`}
                      data-testid={`lesson-${lesson.id}`}
                    >
                      <div className="mt-1 flex-shrink-0">
                        {isCompleted ? (
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                        ) : isCurrent ? (
                          <div className="w-3 h-3 rounded-full bg-white" />
                        ) : (
                          <div className="w-3 h-3 rounded-full border border-white/40" />
                        )}
                      </div>
                      <p className={`text-sm leading-tight ${
                        isCurrent ? "text-white font-semibold" : "text-white/70"
                      }`}>
                        {lesson.title}
                      </p>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* CENTER: Video Player */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="bg-black w-full relative" style={{ aspectRatio: "16/9" }}>
            {currentLesson.video_type === "mux" ? (
              <MuxLessonPlayer
                courseId={course.id}
                lessonId={currentLesson.id}
                title={currentLesson.title}
              />
            ) : (
              <iframe
                src={getVideoEmbedUrl(currentLesson.video_url, currentLesson.video_type)}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="video-player"
              />
            )}
            <VideoWatermark label={user?.email} />
          </div>

          {/* Materials below video (mobile) */}
          <div className="lg:hidden overflow-y-auto flex-1 bg-[#1a1a1a]">
            <div className="p-4 space-y-4">
              <h2 className="text-xl font-bold text-white">{currentLesson.title}</h2>
              <Button
                onClick={() => markAsCompleted(currentLesson.id)}
                disabled={isLessonCompleted(currentLesson.id)}
                variant="outline"
                className={`w-full h-12 font-semibold ${
                  isLessonCompleted(currentLesson.id)
                    ? "border-green-500/50 text-green-400"
                    : "border-white/30 text-white hover:bg-white/10"
                }`}
                data-testid="mark-complete-btn-mobile"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                {isLessonCompleted(currentLesson.id) ? "Completada" : "Marcar como completado"}
              </Button>
              {currentLesson.description && (
                <p className="text-white/60 text-sm leading-relaxed">{currentLesson.description}</p>
              )}
              {currentLesson.materials?.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4" /> Materiales
                  </h3>
                  {currentLesson.materials.map((m, i) => (
                    <a key={i} href={m.url} target="_blank" rel="noopener noreferrer"
                       className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
                      <Download className="w-4 h-4 text-[#E50914]" />
                      <span className="text-white text-sm">{m.title || `Material ${i + 1}`}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: Lesson Info (desktop only) */}
        <div className="hidden lg:flex lg:w-80 xl:w-96 flex-col bg-[#1a1a1a] border-l border-white/10 overflow-y-auto flex-shrink-0" data-testid="lesson-detail-panel">
          <div className="p-6 space-y-5">
            {/* Lesson Title */}
            <h2 className="text-xl font-bold text-white leading-snug" data-testid="lesson-title">
              {currentLesson.title}
            </h2>

            {/* Mark as Complete */}
            <Button
              onClick={() => markAsCompleted(currentLesson.id)}
              disabled={isLessonCompleted(currentLesson.id)}
              variant="outline"
              className={`w-full h-12 font-semibold transition-all ${
                isLessonCompleted(currentLesson.id)
                  ? "border-green-500/50 text-green-400 bg-green-500/5"
                  : "border-white/30 text-white hover:bg-white/10"
              }`}
              data-testid="mark-complete-btn"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              {isLessonCompleted(currentLesson.id) ? "Completada" : "Marcar como completado"}
            </Button>

            {/* Description */}
            {currentLesson.description && (
              <div className="text-white/60 text-sm leading-relaxed">
                {currentLesson.description}
              </div>
            )}

            {/* Duration */}
            {currentLesson.duration_minutes && (
              <p className="text-white/40 text-xs">
                Duracion: {currentLesson.duration_minutes} minutos
              </p>
            )}

            {/* Materials */}
            {currentLesson.materials?.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-white/10">
                <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-[#E50914]" />
                  Materiales Descargables
                </h3>
                {currentLesson.materials.map((material, index) => (
                  <a
                    key={index}
                    href={material.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4 text-[#E50914]" />
                    <span className="text-white text-sm">{material.title || `Material ${index + 1}`}</span>
                  </a>
                ))}
              </div>
            )}

            {/* General course materials */}
            {course?.course_materials?.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-white/10">
                <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-[#E50914]" />
                  Material del Curso
                </h3>
                {course.course_materials.map((material, index) => (
                  <a
                    key={`course-mat-${index}`}
                    href={material.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4 text-[#E50914]" />
                    <span className="text-white text-sm">{material.title || `Material ${index + 1}`}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Player;
